import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';

import { ChecklistAnsweredModel } from "../../../shared/model/checklist-answered.model";
import { MessageComponent } from "../../../shared/component/message/message.component";
import { ReportService } from "../../../shared/service/report.service";
import { QuestionModel } from '../../../shared/model/question.model';
import { UserService } from '../../../shared/service/user.service';

import { OperationTypes, OperationTypesTranslation } from '../../../shared/enum/operation-types';
import { AnalisyStatusTranslation } from '../../../shared/enum/conclusion-type';
import { ActionPlanModel } from '../../../shared/model/action-plan.model';
import { AttachmentModel } from '../../../shared/model/attachment.model';
import { environment } from "../../../../environments/environment";
import { UserModel } from '../../../shared/model/user.model';

@Component({
  selector: "app-checklist-answered-detail",
  templateUrl: "./checklist-answered-detail.component.html",
  styleUrls: ["./checklist-answered-detail.component.css"],
})
export class ChecklistAnsweredDetailComponent implements OnInit {  
  @ViewChild(MessageComponent) message: MessageComponent;
  @ViewChild("report") report: ElementRef;

  operations = OperationTypesTranslation;

  formatNumber = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 });
  textCenter = {'text-align': 'center'};

  params: any;
  id: number = null;
  creating: boolean = false;
  existPostObservation = false;

  booleanPercentage = {};
  showQuestionGroup = {};

  customLogo: any = environment.customLogo;
  uploadPath: string = localStorage.getItem("apiIP") + "/upload";

  response: ChecklistAnsweredModel = new ChecklistAnsweredModel();
  analisyAttachment = {} as any;

  groupedMeasures: any[][];
  measuresTables: any = {};
  mappedOrders: any[] = [];
  users: UserModel[] = [];
  measureTablesReport: any[] = [];

  loadingMeasureTables = true;
  existActionsPlans = false;
  loadingMeasures = true;

  // For Customer View
  coloredMeasureResponses = {};

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private reportService: ReportService,
    private translateService: TranslateService,
  ) {    
    this.getUsers();
  }

  goToActionPlanURL(): void { // goToReportURL
    const urlTree = this.router.createUrlTree(['action-plan'],
      { queryParams: { checklist_response_id: this.id } }
    );

    const relativeUrl = this.router.serializeUrl(urlTree);
    const baseUrl = window.location.origin;

    window.open(
      baseUrl + '/#' + relativeUrl,
      '_blank'
    );
  }

  getUsers() {
    this.userService.getPaginated(0, 1e+9, "", "", "", true)
    .subscribe((users: { rows: UserModel[] }) => {
      this.users = users['rows'];
    });
  }

  loadVisibleGroups() {
    if (this.response.question_groups && this.response.question_groups.length) {
      this.response.question_groups.forEach((_, i) => this.canShowQuestionGroup(i));
    }
  }

  getLogo() {
    const { logo } = this.response;
    const completePath = this.uploadPath + '/checklist-attachment/';

    if (logo && logo.base64)
    return `data:image/png;base64,${logo.base64}`;
    else if (logo && !logo.base64 && logo.compressed)
    return completePath + this.pathResolver(logo, 'COMPRESSED.jpg');
    else if (logo && !logo.base64 && this.pathResolver(logo))
    return completePath + this.pathResolver(logo);
  }

  canShowInfoGroup() {
    if (!this.response.observation_visible) return false;

    return this.response.checklist_origin && this.response.checklist_origin.description ? true : false;
  }

  pathResolver = (attachment: AttachmentModel, fileName?: string) => 
  attachment && attachment.generated_name && attachment.generated_name.length ?
  attachment.generated_name.split('.')[0] + '/' + (fileName || attachment.generated_name) : '';

  loadPercentage() {
    const { real_responses } = this.response;
    const count = real_responses.length;
    const types = ['true', 'false'];

    for (const type of types) {
      const values = real_responses.filter(({response}) => type == response);
      const total = values.length * 100 / count;

      if (total)
      this.booleanPercentage[type] = total;

      this.booleanPercentage[type + '_count'] = values.length || 0;
    }
  }

  getCorrectDescription(boolean, groupIndex) {
    const { checklist_origin } = this.response;
    const { question_groups } = checklist_origin;
    const question = question_groups[groupIndex].questions[0];
    const { okay_description, disapproved_description } = question;

    return boolean == 'true' ? okay_description || "A" :  disapproved_description || "R";
  }

  getPercentage(filter: string, groupIndex: string, getGeneral = false) {
    const { real_responses, question_groups } = this.response;
    const count = real_responses.length;
    const filtered = [];

    question_groups[groupIndex].questions.forEach(({question_id}) => {
      real_responses.forEach((response) => {
        if (response.question_id == question_id) {
          filtered.push(response)
        }
      })
    });

    const values = filtered.filter(({response}) => response == filter).length;

    const totalByGroup = values ? values * 100 / filtered.length : 0;
    const generalTotal = values ? values * 100 / count : 0;

    // getGeneral compares with all group and values
    return getGeneral ? generalTotal.toFixed(2) : totalByGroup.toFixed(2);
  }

  getTotalPercentage(filter: string) {
    if (filter && this.booleanPercentage && this.booleanPercentage[filter])
    return this.booleanPercentage[filter].toFixed(2);
    else return 0;
  }

  isUnconformityOnly = () => this.params && this.params.unconformities_only == 'true';

  isValidResponse = (response: string) => response && response != '0';

  convertToFloat = (value: number, count: number = 2) => value ? (value * 10).toFixed(count) : '00.00';

  existAttachments = (question) => question && question.attachments && question.attachments.length > 0;

  existActionPlans = (question) => {
    const exist = question && question.action_plans && question.action_plans.length > 0;
    this.existActionsPlans = this.existActionsPlans || exist;
    return exist;
  };

  existUnconformityResponses = (q) => q && q.question_unconformity_responses && q.question_unconformity_responses.length > 0;
  existInspectionUnconformityResponses = (ir) => ir && ir.unconformity_responses && ir.unconformity_responses.length > 0;

  canShowQuestionGroup(index: number) {
    const isUnconformityOnly = this.isUnconformityOnly();

    this.showQuestionGroup[index] =
    this.response.question_groups[index].questions &&
    this.response.question_groups[index].questions.length &&
    this.response.question_groups[index].questions.some((q: QuestionModel) => {
      const realData = this.response.real_responses.find(r => r.question_id == q.question_id);

      const existUnconformities = this.existUnconformityResponses(q) || this.existActionPlans(q);
      const validateUnconformity = isUnconformityOnly ? existUnconformities : this.isValidResponse(q.response);

      return q.always_visible || validateUnconformity || realData && realData.response == 'false';
    }) || false;
  }

  canShowQuestion(question: QuestionModel) {
    const existUnconformities = this.existUnconformityResponses(question) || this.existActionPlans(question);
    const realData = this.response.real_responses.find(r => r.question_id == question.question_id);

    if (this.isUnconformityOnly()) return existUnconformities || realData && realData.response == 'false';
    return this.isValidResponse(question.response) || existUnconformities;
  }

  getActionPlanAttachments({ action_plans }) {
    if (action_plans && action_plans.length)
    return action_plans.map((ap) => ap.attachments).flatMap(v => v);
    return [];
  }

  // unused
  getQuestionUnconformityAttachments({ question_unconformity_responses }) {
    if (question_unconformity_responses && question_unconformity_responses.length)
    return question_unconformity_responses.map((ap) => ap.attachments).flatMap(v => v);
    return [];
  }

  existQuestions = () =>
    this.response.question_groups &&
    this.response.question_groups[0] &&
    this.response.question_groups[0].questions &&
    this.response.question_groups[0].questions.length > 0;

  sumValues(array: { count: number }[], result = 0) {
    array.forEach((value) => result += value && value.count || 0);
    return result ? result.toFixed(2) || 0 : 0;
  }

  ngOnInit() {
    this.creating = true;

    this.translateService.get('version').pipe(take(1)).subscribe(_ =>
      this.message.info("generating_report_msg")
    );

    const id = Number(this.route.snapshot.paramMap.get("id"));

    this.route.queryParams
      .filter((params) => params.unconformities_only || params.big_size)
      .subscribe((params) => {
        this.params = params;
      });

    if (id) {
      this.reportService.getChecklistAnswered(id, this.params).subscribe(
        (result) => {
          this.response = result;
          this.creating = false;

          this.loadPercentage();
          this.loadVisibleGroups();

          this.existPostObservation = result.post_observation != null;

          if (result.checklist_origin.is_order) {
            this.setOrderByIndex(result.orders);
            this.loadMeasureGroups(result.measure_responses);
          }

          if (result.measure_tables_report && result.measure_tables_report.length) {
            this.measureTablesReport = result.measure_tables_report;
            console.log('[MeasureTable] estrutura:', JSON.stringify(result.measure_tables_report[0], null, 2));
          }

          this.message.clear();
          this.message.success("success_generate_report");
          return;
        },
        (error) => {
          this.message.error("error_generate_report");
          console.error(error);
        }
      );

      this.id = id;
    }
  }

  // Set the ordernation to Order List
  // Add a separator on Order Id Change
  setOrderByIndex(list: any[]) {
    // list = orderBy(list, 'order_id'); // NT

    let lastItemId: number = 0;
    let lastIndex: number = 0;

    for (let currentIndex in list) {
      let currentItemId = list[currentIndex].order_id;

      // For separator
      if (lastItemId == 0) {
        lastItemId = currentItemId;
        lastIndex = -1;
      }

      // For ordenation
      if (lastItemId == currentItemId) {
        list[currentIndex].break = false;
        lastIndex += 1;
      } else {
        list[currentIndex].break = true;
        lastIndex = 0;
      }

      list[currentIndex].position = lastIndex;
      lastItemId = currentItemId;
    }

    return list;
  }

  getColoredMeasureResponses(measure_table, measure_grid_string, measure_response_grid_string) {
    const mtIndex = measure_table.id;

    if (this.coloredMeasureResponses[mtIndex]) {
      return this.coloredMeasureResponses[mtIndex];
    }

    const measureResponseGrid = this.mapStringToNumberArray(measure_response_grid_string);
    const measureGrid = this.mapStringToNumberArray(measure_grid_string);

    if (measureGrid && measureResponseGrid) {
      for (const index in measureGrid) {
        const response = measureResponseGrid[index];
        const measure = measureGrid[index];

        const max = measure + measure_table.tolerance;
        const min = measure - measure_table.tolerance;

        let updatedMeasure = { color: 'yellow', measure, response, index: parseInt(index) + 1 };

        if (response <= max && response >= min) {
          updatedMeasure.color = 'green';
        } else {
          updatedMeasure.color = 'red';
        }

        // Minimal NullSafetly Validation
        if (!this.coloredMeasureResponses[mtIndex])
        this.coloredMeasureResponses[mtIndex] = [];

        this.coloredMeasureResponses[mtIndex].push(updatedMeasure);
      }
    }

    return this.coloredMeasureResponses[mtIndex];
    // return this.getColoredMeasureResponses(measure_table, measure_grid_string, measure_response_grid_string);

    // if (this.coloredMeasureResponses[measure_table.measure_table_id])
    // return this.coloredMeasureResponses[measure_table.measure_table_id].map((cmr, i) => `
    //   M${i+1}: ${cmr.measure}
    //   <span style="color: ${cmr.color}">
    //     R: ${cmr.response}
    //   </span>
    // `).join(' - ')
  }

  formatMeasure = (measures: string) => measures && this.mapStringToNumberArray(measures).map((m, i) => `M${i+1}: ${m}`).join(' - ');

  mapStringToNumberArray = (grid: string) => grid.split(',').map(n => parseFloat(n)).filter(v => v);

  loadMeasureGroups(measure_responses) {
      this.groupedMeasures = [];

      const groupedMeasures = {};
      const measureTableIds = {};

      for (const measure of measure_responses) {
          if (!measure) return console.error('Measure Group Error');

          const uid = measure.measure_table_id;

          if (!groupedMeasures[uid]) groupedMeasures[uid] = [];

          if (measure.measure_grid && measure.measure.measure_grid) {
            measure.measures = this.mapStringToNumberArray(measure.measure_grid);
            measure.measure.measures = this.mapStringToNumberArray(measure.measure.measure_grid);
          } else {
            console.error('Error on Measure Grid:', measure.measure_grid, measure.measure.measure_grid);
          }

          groupedMeasures[uid].push(measure);
          measureTableIds[measure.measure_table_id] = true;
      }

      Object.getOwnPropertyNames(groupedMeasures).forEach(group => {
          this.groupedMeasures.push(groupedMeasures[group]);
      });

      this.loadingMeasures = false;

      this.getMeasureTables(Object.getOwnPropertyNames(measureTableIds));
  }

  getMeasureTables(measure_table_ids) {
    if (measure_table_ids && measure_table_ids.length) {
      this.reportService.getFilteredMeasureTables({ measure_table_ids }).subscribe(
        (result) => {
          for (const table of result) {
            this.measuresTables[table.measure_table_id] = table;
          }

          this.loadingMeasureTables = false;
        },
        (error) => {
          this.message.error("error_get_measure_tables");
          console.error(error);
        }
      );
    }
  }

  getFullPath(folder: string, attachment: AttachmentModel) {
    let completePath = '';

    // for shared component
    const pathResolver = (attachment: AttachmentModel, fileName?: string) => 
    attachment && attachment.generated_name && attachment.generated_name.length ?
    attachment.generated_name.split('.')[0] + '/' + (fileName || attachment.generated_name) : '';

    if (attachment) {
      // let ext = attachment.generated_name.split('.').pop();
      let uploadPath = `${this.uploadPath}/${folder}-attachment/`;
      let legacyPath = `${this.uploadPath}/unconformity-attachment/`;

      // Legacy Attachment Field
      if (attachment.belongs_to_unconformity && !attachment.compressed)
      completePath = legacyPath + pathResolver(attachment);
      else if (attachment.belongs_to_unconformity && attachment.compressed)
      completePath = legacyPath + pathResolver(attachment, 'COMPRESSED.jpg');

      else if (attachment.compressed)
      completePath = uploadPath + pathResolver(attachment, 'COMPRESSED.jpg');
      // else if (attachment.resized)
      // completePath = uploadPath + pathResolver(attachment, 'RESIZED.' + ext);
      else 
      completePath = uploadPath + pathResolver(attachment);
    }

    return completePath;
  }

	updateAnalisy(status) {
		if (this.id || this.response.id) {
      this.reportService.updateAnalisy({
        id: this.id || this.response.id, status,
        resp_observation: this.response.resp_observation,
        analisy_status: AnalisyStatusTranslation['finished_analisy'],
        analisy_attachment: this.analisyAttachment
      })
      .pipe(take(1)).subscribe((res: number[]) => {
        if (res && res.length) {
          this.message.success('updated');
          window.location.reload(); // Refresh screen
        } else {
          this.message.error('status_error');
        }
      });
    }
	};

	savePostObservation() {
		if (this.id || this.response.id) {
      this.reportService.postObservation({
        id: this.id || this.response.id,
        post_observation: this.response.post_observation
      })
      .pipe(take(1)).subscribe((res: number[]) => {
        if (res && res.length) {
          this.message.success('updated');
          window.location.reload(); // Refresh screen
        } else {
          this.message.error('status_error');
        }
      });
    }
	};

  updateColorByOperation(question) {
    const [min, max] = question.operation_value.split('-').map(n => parseFloat(n));
    const operation_value = parseFloat(question.operation_value);
    const operation_type = parseFloat(question.operation_type);
    const value = parseFloat(question.response);

    let color = 'red';

    const validation = (n: number) => n || n === 0;

    // foi utilizado if em vez do switch, 
    // por que já vi bugs no check em switchs corretos.
    // para prevenir isso, não utilizei switch nesse trecho
    if (validation(operation_type) && validation(value)) {
      if (operation_type == OperationTypes['Entre']) {
        if (value >= min && value <= max) color = 'green';
      }
  
      if (operation_type == OperationTypes['Igual a']) {
        if (value == operation_value) color = 'green';
      }
  
      if (operation_type == OperationTypes['Menor que']) {
        if (value < operation_value) color = 'green';
      }
  
      if (operation_type == OperationTypes['Maior que']) {
        if (value > operation_value) color = 'green';
      }
  
      if (operation_type == OperationTypes['Igual ou menor que']) {
        if (value <= operation_value) color = 'green';
      }
  
      if (operation_type == OperationTypes['Igual ou maior que']) {
        if (value >= operation_value) color = 'green';
      }
    }

    return color;
  }

  // For signature
  getUserNameById(id: number) {
    const user = this.users.length && this.users.find(u => u.id == id);
    return id && user && user.name || `Deleted User: ${id}`;
  }

  getUserAttachmentById(id: number) {
    const user = this.users.length && this.users.find(u => u.id == id);
    return id && user && user.signature_image;
  }

	onSelectMidia(e) {
		if (e.files && this.id) {
			Array.from(e.files).forEach((item: Blob | any, i, arr) => {
        const type = item.type && item.type.length > 0 ? item.type : item.name.split('.').pop();
				const reader = new FileReader();

				reader.readAsDataURL(item);
				reader.onload = () => {
					this.analisyAttachment= {
            checklist_response_id: this.id,
            base64: reader.result,
						size: item.size,
						name: item.name,
						file_type: type,
					};

					return;
				};
			});
    }
	}

	onClearMidia(e) {
		this.analisyAttachment = {} as any;
	}

  getAnalisyStatus = () => AnalisyStatusTranslation[this.response.analisy_status];

  bigSize = () => this.params && this.params['big_size'] == 'true' ? 'big-size' : 'attachment';

  getMeasureRowColor(inTolerance: boolean | null): string {
    if (inTolerance === true)  return '#27ae60';
    if (inTolerance === false) return '#e74c3c';
    return '#f39c12';
  }

  getMeasureRowBg(inTolerance: boolean | null): string {
    if (inTolerance === true)  return '#eafaf1';
    if (inTolerance === false) return '#fdedec';
    return '#fef9e7';
  }

  getMeasureRowIcon(inTolerance: boolean | null): string {
    if (inTolerance === true)  return '✔';
    if (inTolerance === false) return '✘';
    return '—';
  }

  translate = (key: string) => this.translateService.instant(key || 'invalid');
  tUpper = (key) => this.translate(key).toString().toUpperCase();
}



// WEBPACK FOOTER //
// C:/Users/Emanuel/Documents/EasyCheck/easy-check-front-web/src/app/report/checklist-answered/checklist-answered-detail/checklist-answered-detail.component.ts